import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { of, switchMap, throwError } from 'rxjs';
import { MockApiService } from '../services/mock-api.service';
import { AdminSessionService } from '../services/admin-session.service';
import { AdminUserCreateRequest, AdminUserUpdateRequest } from '@core/models';
import { shouldUseMockApi } from '../utils/mock-api-routing.util';

/**
 * HTTP interceptor that routes requests to the mock API service when
 * shouldUseMockApi() returns true (see mock-api-routing.util.ts).
 */
export const mockInterceptor: HttpInterceptorFn = (req, next) => {
  if (!shouldUseMockApi(req.url)) {
    return next(req);
  }

  const mockApi = inject(MockApiService);
  const url = req.url;
  const method = req.method;
  const body = req.body as Record<string, unknown>;

  // Route to appropriate mock handler
  if (url.includes('/contract/authenticate') && method === 'POST') {
    return mockApi
      .authenticateContract(
        body['vin7'] as string,
        body['lastName'] as string,
        body['zip'] as string,
      )
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  if (url.includes('/otp/send') && method === 'POST') {
    return mockApi
      .sendOtp(body['otpChallengeId'] as string)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  if (url.includes('/otp/verify') && method === 'POST') {
    return mockApi
      .verifyOtp(body['otpChallengeId'] as string, body['code'] as string)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  if (url.includes('/vin/decode') && method === 'POST') {
    return mockApi
      .decodeVin(body['vin'] as string)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  if (url.includes('/vin/eligibility') && method === 'POST') {
    return mockApi
      .checkEligibility(body['vin'] as string)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  if (url.match(/\/exceptions(\?|$)/) && method === 'POST') {
    return mockApi
      .submitException(
        body['secondaryVin'] as string,
        body['customerFirstName'] as string,
        body['customerLastName'] as string,
        body['customerEmail'] as string,
        body['vinAddRequestId'] as string | undefined,
      )
      .pipe(switchMap((response) => of(new HttpResponse({ status: 201, body: response }))));
  }

  if (url.includes('/vin/commit') && method === 'POST') {
    const idempotencyKey = req.headers.get('X-Idempotency-Key') || '';
    return mockApi
      .commitVin(
        body['vin'] as string,
        body['acceptIrreversible'] as boolean,
        idempotencyKey,
        body['customerFirstName'] as string,
        body['customerLastName'] as string,
        body['customerEmail'] as string,
      )
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  if (url.match(/\/vin\/request\/[^/]+$/) && method === 'GET') {
    const requestId = url.split('/').pop() || '';
    return mockApi
      .getRequestStatus(requestId)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  if (url.includes('/exceptions') && method === 'POST') {
    return mockApi
      .submitException(
        body['secondaryVin'] as string,
        body['customerFirstName'] as string,
        body['customerLastName'] as string,
        body['customerEmail'] as string,
        body['vinAddRequestId'] as string | undefined,
      )
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  // Document endpoints
  if (url.match(/\/document\/request\/[^/]+\/pdf$/) && method === 'GET') {
    const parts = url.split('/');
    const requestId = parts[parts.length - 2];
    return mockApi.generateDocument(requestId).pipe(
      switchMap((blob) =>
        of(
          new HttpResponse({
            status: 200,
            body: blob,
          }),
        ),
      ),
    );
  }

  if (url.match(/\/document\/request\/[^/]+\/email$/) && method === 'POST') {
    const parts = url.split('/');
    const requestId = parts[parts.length - 2];
    return mockApi
      .emailDocument(requestId, body['email'] as string)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  // Admin denial reasons report (ops-reports-001) — must come before generic /admin/exceptions matches
  if (url.includes('/admin/reports/denial-reasons') && method === 'GET') {
    const urlObj = new URL(url, window.location.origin);
    const adminSession = inject(AdminSessionService);
    const generatedBy = adminSession.displayName() ?? adminSession.email() ?? 'Admin User';
    return mockApi
      .getDenialReasonsReport(
        {
          range: urlObj.searchParams.get('range') ?? '30',
          store: urlObj.searchParams.get('store') ?? 'all',
          from: urlObj.searchParams.get('from') ?? undefined,
          to: urlObj.searchParams.get('to') ?? undefined,
        },
        generatedBy,
      )
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  // Admin exception dashboard (ops-dashboard-001)
  if (url.includes('/admin/exceptions/dashboard') && method === 'GET') {
    const urlObj = new URL(url, window.location.origin);
    return mockApi
      .getExceptionDashboard({
        range: urlObj.searchParams.get('range') ?? '30',
        store: urlObj.searchParams.get('store') ?? 'all',
        from: urlObj.searchParams.get('from') ?? undefined,
        to: urlObj.searchParams.get('to') ?? undefined,
      })
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  // Admin exception detail (ops-drawer-001) — must come before generic queue match
  if (url.match(/\/admin\/exceptions\/[^/]+(\?|$)/) && method === 'GET') {
    const path = url.split('?')[0];
    const id = path.split('/').pop() || '';
    return mockApi
      .getExceptionDetail(id)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  // Admin exception approve / deny (ops-dashboard-001, ops-approve, ops-deny)
  if (url.match(/\/admin\/exceptions\/[^/]+\/approve$/) && method === 'POST') {
    const parts = url.split('/');
    const id = parts[parts.length - 2];
    return mockApi.approveException(id).pipe(switchMap((response) => toHttpResult(response, url)));
  }
  if (url.match(/\/admin\/exceptions\/[^/]+\/deny$/) && method === 'POST') {
    const parts = url.split('/');
    const id = parts[parts.length - 2];
    return mockApi
      .denyException(id, body?.['failureReason'] as string)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  // Admin exception queue list (ops-queue-001) — must come after dashboard/approve/deny patterns
  if (url.match(/\/admin\/exceptions(\?|$)/) && method === 'GET') {
    const urlObj = new URL(url, window.location.origin);
    return mockApi
      .getExceptionQueue({
        status: urlObj.searchParams.get('status') ?? 'all',
        search: urlObj.searchParams.get('search') ?? '',
        range: urlObj.searchParams.get('range') ?? '30',
        store: urlObj.searchParams.get('store') ?? 'all',
        from: urlObj.searchParams.get('from') ?? undefined,
        to: urlObj.searchParams.get('to') ?? undefined,
      })
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  // Admin user management (ops-users-001) — must come before generic /admin matches
  if (url.match(/\/admin\/users(\?|$)/) && method === 'GET') {
    return mockApi.getAdminUsers().pipe(switchMap((response) => toHttpResult(response, url)));
  }

  if (url.match(/\/admin\/users(\?|$)/) && method === 'POST') {
    return mockApi
      .createAdminUser(body as unknown as AdminUserCreateRequest)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  if (url.match(/\/admin\/users\/[^/?]+$/) && method === 'PUT') {
    const userId = url.split('/').pop() || '';
    return mockApi
      .updateAdminUser(userId, body as unknown as AdminUserUpdateRequest)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  if (url.match(/\/admin\/users\/[^/?]+$/) && method === 'DELETE') {
    const userId = url.split('/').pop() || '';
    return mockApi
      .deleteAdminUser(userId)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  // Admin system logs / audit events (ops-logs-001)
  if (url.match(/\/admin\/audit-events(\?|$)/) && method === 'GET') {
    const urlObj = new URL(url, window.location.origin);
    const type = (urlObj.searchParams.get('type') ?? 'all') as
      | 'all'
      | 'EXCEPTION_SUBMITTED'
      | 'ELIGIBILITY_CHECK'
      | 'CASE_CREATED'
      | 'DECISION_RECORDED'
      | 'VIN_COMMIT'
      | 'AUTH_SUCCESS';
    return mockApi
      .getAdminAuditEvents({
        type,
        range: urlObj.searchParams.get('range') ?? '30',
        store: urlObj.searchParams.get('store') ?? 'all',
        from: urlObj.searchParams.get('from') ?? undefined,
        to: urlObj.searchParams.get('to') ?? undefined,
      })
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  // Admin auth
  if (url.includes('/admin/auth/login-config') && method === 'GET') {
    return of(
      new HttpResponse({
        status: 200,
        body: {
          correlationId: 'mock-login-config',
          success: true,
          data: { loginType: 'password' },
          error: null,
        },
      }),
    );
  }

  if (url.includes('/admin/auth/dev-login') && method === 'POST') {
    return mockApi
      .devLogin(body['email'] as string, body['password'] as string)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  if (url.includes('/admin/auth/sso-callback') && method === 'POST') {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 403,
          statusText: 'Forbidden',
          url,
          error: {
            correlationId: 'mock-sso',
            success: false,
            data: null,
            error: { code: 'FORBIDDEN', message: 'SSO callback not yet implemented' },
          },
        }),
    );
  }

  // Admin contract detail by ID
  if (url.match(/\/admin\/contracts\/[\w-]+$/) && method === 'GET') {
    const contractContextId = url.split('/').pop() || '';
    return mockApi
      .getContractDetail(contractContextId)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  // Admin search (with query params, no path segment after /contracts)
  if (
    url.includes('/admin/contracts') &&
    !url.match(/\/admin\/contracts\/[\w-]+/) &&
    method === 'GET'
  ) {
    const urlObj = new URL(url, window.location.origin);
    return mockApi
      .searchContracts({
        search: urlObj.searchParams.get('search') ?? undefined,
        page: urlObj.searchParams.get('page') ? Number(urlObj.searchParams.get('page')) : undefined,
        limit: urlObj.searchParams.get('limit')
          ? Number(urlObj.searchParams.get('limit'))
          : undefined,
        sort:
          (urlObj.searchParams.get('sort') as
            'createdAt' | 'externalContractId' | 'status' | null) ?? undefined,
        order: (urlObj.searchParams.get('order') as 'ASC' | 'DESC' | null) ?? undefined,
      })
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  if (url.match(/\/admin\/requests\/[^/]+\/document\/pdf$/) && method === 'GET') {
    const parts = url.split('/');
    const requestId = parts[parts.length - 3];
    return mockApi
      .generateDocument(requestId)
      .pipe(switchMap((blob) => of(new HttpResponse({ status: 200, body: blob }))));
  }

  if (url.match(/\/admin\/requests\/[^/]+\/document\/generate$/) && method === 'POST') {
    const parts = url.split('/');
    const requestId = parts[parts.length - 3];
    return of(
      new HttpResponse({
        status: 200,
        body: {
          correlationId: 'mock-corr',
          success: true,
          data: {
            status: 'available',
            documentAvailable: true,
            canGenerate: true,
            canDownload: true,
            emailStatus: null,
            message: 'Confirmation document is available to download or regenerate.',
            requestId,
          },
          error: null,
        },
      }),
    );
  }

  if (url.match(/\/admin\/requests\/[^/]+\/document$/) && method === 'GET') {
    const parts = url.split('/');
    const requestId = parts[parts.length - 2];
    return of(
      new HttpResponse({
        status: 200,
        body: {
          correlationId: 'mock-corr',
          success: true,
          data: {
            status: 'available',
            documentAvailable: true,
            canGenerate: true,
            canDownload: true,
            emailStatus: null,
            message: '',
            requestId,
          },
          error: null,
        },
      }),
    );
  }

  if (url.match(/\/admin\/requests\/[^/]+$/) && method === 'GET') {
    const requestId = url.split('/').pop() || '';
    return mockApi
      .getRequestDetail(requestId)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  if (url.match(/\/admin\/requests\/[^/]+\/note$/) && method === 'POST') {
    const parts = url.split('/');
    const requestId = parts[parts.length - 2];
    return mockApi
      .addNote(requestId, body['note'] as string)
      .pipe(switchMap((response) => toHttpResult(response, url)));
  }

  // If no mock handler matches, pass through to real API
  return next(req);
};

/**
 * Convert a mock API response to an HttpResponse (success) or throw HttpErrorResponse (error).
 * Angular's HttpClient only triggers the error callback for thrown errors, not for
 * HttpResponse objects with non-2xx status codes returned from interceptors.
 */
function toHttpResult(
  response: { success: boolean; error?: { code?: string } | null },
  url: string,
) {
  if (response.success) {
    return of(new HttpResponse({ status: 200, body: response }));
  }
  const status = getErrorStatus(response.error?.code);
  return throwError(() => new HttpErrorResponse({ status, error: response, url }));
}

/**
 * Map error codes to HTTP status codes
 */
function getErrorStatus(code?: string): number {
  const statusMap: Record<string, number> = {
    AUTH_NO_MATCH: 401,
    AUTH_OTP_REQUIRED: 403,
    AUTH_EXPIRED: 401,
    AUTH_INVALID: 401,
    RATE_LIMITED: 429,
    OTP_INVALID: 400,
    OTP_EXPIRED: 400,
    OTP_LOCKED_OUT: 429,
    VIN_INVALID_FORMAT: 400,
    VIN_DECODE_FAILED: 400,
    VIN_INELIGIBLE: 422,
    VIN_ALREADY_COMMITTED: 409,
    CONTRACT_LOCKED: 409,
    CONTRACT_NOT_FOUND: 404,
    DEPENDENCY_UNAVAILABLE: 503,
    INTERNAL_ERROR: 500,
  };

  return statusMap[code || ''] || 500;
}
